import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Table, Alert, Spinner, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap';
import Select from 'react-select';
import { issuedInventoryAPI } from '../api/issuedInventoryAPI';
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI';
import { vendorAPI } from '../api/vendorAPI';
import { getParentDestinationOptions, getSubcategoryOptions, formatDestinationDisplayName } from '../utils/issueUtils';
import { formatCurrency, formatUnitCost, formatDate, formatQuantity } from '../utils/commonUtils';
import PaginationComponent from '../components/Pagination';
import { usePermissions } from '../contexts/PermissionContext';
import SearchInputWithClear from '../components/SearchInputWithClear';
import StockItemInfo from '../components/StockItemInfo';
import '../styles/selectControl.css';

function IssuedInventoryReportScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reportData, setReportData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [parentDestinationOptions, setParentDestinationOptions] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState([]);
    const [destinationsLoading, setDestinationsLoading] = useState(true);
    const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
    
    // Manufacturer filter
    const [vendorMap, setVendorMap] = useState({});
    const [selectedManufacturer, setSelectedManufacturer] = useState('');

    // Form state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]);

    // Multiple destination-subcategory filters
    const [destinationFilters, setDestinationFilters] = useState([]);
    const [currentParentDestination, setCurrentParentDestination] = useState('');
    const [currentSubcategory, setCurrentSubcategory] = useState('');
    
    // Aggregation toggle
    const [aggregateByItem, setAggregateByItem] = useState(
        () => sessionStorage.getItem('issuedInventoryAggregateByItem') === 'true'
    );

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Set default date range (last 30 days)
    useEffect(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        
        setEndDate(today.toISOString().split('T')[0]);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }, []);

    // Fetch vendors for manufacturer filter
    useEffect(() => {
        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll();
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name;
                    return acc;
                }, {});
                setVendorMap(mapping);
            } catch (error) {
                console.error('Error fetching vendors:', error);
            }
        }
        fetchVendors();
    }, []);

    // Fetch categories for filter
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                // Call with large page size to get all categories
                const response = await stockItemCategoryAPI.getAll(1, 1000, '');
                setCategories(response.results || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        };
        fetchCategories();
    }, []);

    // Load parent destination options
    useEffect(() => {
        const loadDestinations = async () => {
            try {
                setDestinationsLoading(true);
                const options = await getParentDestinationOptions();
                setParentDestinationOptions(options);
                
                // Don't auto-select any destination - leave as empty for "All Destinations"
            } catch (error) {
                console.error('Error loading destinations:', error);
                setError('Failed to load destination options');
            } finally {
                setDestinationsLoading(false);
            }
        };
        loadDestinations();
    }, []);

    // Load subcategories when current parent destination changes
    useEffect(() => {
        const loadSubcategories = async () => {
            if (currentParentDestination) {
                try {
                    setSubcategoriesLoading(true);
                    const options = await getSubcategoryOptions(currentParentDestination);
                    setSubcategoryOptions(options);
                } catch (error) {
                    console.error('Error loading subcategories:', error);
                    setSubcategoryOptions([{ value: '', label: 'Error loading subcategories', type: 'error' }]);
                } finally {
                    setSubcategoriesLoading(false);
                }
            } else {
                setSubcategoryOptions([]);
                setCurrentSubcategory(''); // Clear subcategory selection
            }
        };
        loadSubcategories();
    }, [currentParentDestination]);

    // Re-generate report when aggregate toggle changes (skip initial mount)
    const aggregateInitialMount = useRef(true);
    useEffect(() => {
        if (aggregateInitialMount.current) {
            aggregateInitialMount.current = false;
            return;
        }
        if (reportData) {
            handleGenerateReport();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregateByItem]);

    const handleGenerateReport = async () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates');
            return;
        }

        // Destination is now optional - no validation needed

        setLoading(true);
        setError('');

        try {
            const params = {
                start_date: startDate,
                end_date: endDate,
                page: currentPage,
                pageSize: pageSize
            };

            // Add destination filters - send as JSON array
            if (destinationFilters.length > 0) {
                params.destinationFilters = JSON.stringify(destinationFilters.map(f => ({
                    parentDestinationId: f.parentDestinationId,
                    subcategoryDestinationId: f.subcategoryDestinationId
                })));
            }

            if (selectedCategories.length > 0) {
                params.categories = selectedCategories.join(',');
            }

            // Add search term if provided
            if (searchTerm.trim()) {
                params.searchTerm = searchTerm.trim();
            }

            // Add manufacturer filter if provided
            if (selectedManufacturer) {
                params.manufacturerId = selectedManufacturer;
            }

            // Add aggregation flag
            if (aggregateByItem) {
                params.aggregate = 'true';
            }

            const response = await issuedInventoryAPI.getIssuedInventoryReport(params);
            setReportData(response);
        } catch (error) {
            setError(error.message || 'Failed to generate report');
            console.error('Error generating report:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (format) => {
        if (!startDate || !endDate) {
            setError('Please generate a report first');
            return;
        }

        setLoading(true);

        try {
            // Build categories parameter
            const categoriesParam = selectedCategories.length > 0 ? selectedCategories.join(',') : '';

            // Build destination filters parameter
            const destinationFiltersParam = destinationFilters.length > 0
                ? JSON.stringify(destinationFilters.map(f => ({
                    parentDestinationId: f.parentDestinationId,
                    subcategoryDestinationId: f.subcategoryDestinationId
                })))
                : '';

            const success = await issuedInventoryAPI.generateIssuedInventoryReport(
                startDate,
                endDate,
                destinationFiltersParam,
                categoriesParam,
                searchTerm.trim(),
                format,
                selectedManufacturer || undefined,
                aggregateByItem ? 'true' : undefined
            );

            if (!success) {
                setError('Failed to export report');
            }
        } catch (error) {
            setError(error.message || 'Failed to export report');
            console.error('Error exporting report:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCurrentParentDestinationChange = (event) => {
        setCurrentParentDestination(event.target.value);
        setCurrentSubcategory(''); // Clear subcategory when parent changes
    };

    const handleCurrentSubcategoryChange = (event) => {
        setCurrentSubcategory(event.target.value);
    };

    const handleAddDestinationFilter = () => {
        if (!currentParentDestination) {
            setError('Please select a destination');
            return;
        }

        // Get the destination label - compare as strings
        const parentDest = parentDestinationOptions.find(d => String(d.value) === String(currentParentDestination));

        // Only look for subcategory if one is selected - compare as strings
        const subcatLabel = currentSubcategory
            ? (subcategoryOptions.find(s => String(s.value) === String(currentSubcategory))?.label || '')
            : 'All subcategories';

        const newFilter = {
            parentDestinationId: currentParentDestination,
            parentDestinationLabel: parentDest?.label || '',
            subcategoryDestinationId: currentSubcategory || null,
            subcategoryDestinationLabel: subcatLabel
        };

        // Check if this combination already exists
        const exists = destinationFilters.some(f =>
            f.parentDestinationId === newFilter.parentDestinationId &&
            f.subcategoryDestinationId === newFilter.subcategoryDestinationId
        );

        if (exists) {
            // Silently skip if already exists
            setCurrentParentDestination('');
            setCurrentSubcategory('');
            return;
        }

        setDestinationFilters([...destinationFilters, newFilter]);
        setCurrentParentDestination('');
        setCurrentSubcategory('');
        setError('');
    };

    const handleRemoveDestinationFilter = (index) => {
        setDestinationFilters(destinationFilters.filter((_, i) => i !== index));
    };

    const handleClearAllDestinationFilters = () => {
        setDestinationFilters([]);
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategories(prev => 
            prev.includes(categoryId) 
                ? prev.filter(c => c !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        setSearchTerm(value);
    };

    const handleAllCategoriesChange = () => {
        if (selectedCategories.length === categories.length) {
            // If all are selected, deselect all
            setSelectedCategories([]);
        } else {
            // Select all categories
            setSelectedCategories(categories.map(cat => cat._id));
        }
    };

    const handleAggregateToggle = () => {
        const newValue = !aggregateByItem;
        setAggregateByItem(newValue);
        sessionStorage.setItem('issuedInventoryAggregateByItem', String(newValue));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        // Re-generate report with new page
        setTimeout(() => {
            handleGenerateReport();
        }, 100);
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    if (!hasPermission('reports.generate')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to generate reports.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-4">
            <Row className="mb-3">
                <Col>
                    <h1>Issued Inventory Report</h1>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Filters */}
                            <Form className="mb-4">
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Start Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>End Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Search by GTIN or Product Name</Form.Label>
                                            <SearchInputWithClear
                                                value={searchInputValue}
                                                onChange={handleSearchInputChange}
                                                onClear={handleClearSearch}
                                                placeholder="Enter GTIN or product name..."
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleGenerateReport();
                                                    }
                                                }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Manufacturer</Form.Label>
                                            <Select
                                                value={selectedManufacturer ? { value: selectedManufacturer, label: vendorMap[selectedManufacturer] } : null}
                                                onChange={(opt) => setSelectedManufacturer(opt?.value || '')}
                                                options={Object.entries(vendorMap).map(([id, name]) => ({ value: id, label: name }))}
                                                placeholder="All Manufacturers..."
                                                isClearable
                                                isSearchable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Destination Filters Section */}
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Destination Filters (Optional)</Form.Label>
                                            <div style={{ border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '1rem' }}>
                                                {/* Add new filter form */}
                                                <Row>
                                                    <Col md={5}>
                                                        <Form.Select
                                                            value={currentParentDestination}
                                                            onChange={handleCurrentParentDestinationChange}
                                                            disabled={destinationsLoading}
                                                            size="sm"
                                                        >
                                                            {destinationsLoading ? (
                                                                <option value="">Loading destinations...</option>
                                                            ) : (
                                                                <>
                                                                    <option value="">Select Destination...</option>
                                                                    {parentDestinationOptions
                                                                        .filter(destination => destination.value)
                                                                        .map(destination => (
                                                                            <option key={destination.value} value={destination.value}>
                                                                                {destination.label}
                                                                            </option>
                                                                        ))}
                                                                </>
                                                            )}
                                                        </Form.Select>
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Select
                                                            value={currentSubcategory}
                                                            onChange={handleCurrentSubcategoryChange}
                                                            disabled={!currentParentDestination || subcategoriesLoading}
                                                            size="sm"
                                                        >
                                                            <option value="">All subcategories</option>
                                                            {subcategoriesLoading ? (
                                                                <option value="">Loading subcategories...</option>
                                                            ) : (
                                                                subcategoryOptions.filter(s => s.value && s.type !== 'placeholder').map(subcategory => (
                                                                    <option key={subcategory.value} value={subcategory.value}>
                                                                        {subcategory.label}
                                                                    </option>
                                                                ))
                                                            )}
                                                        </Form.Select>
                                                    </Col>
                                                    <Col md={2} className="d-flex gap-1">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={handleAddDestinationFilter}
                                                            disabled={!currentParentDestination}
                                                            className="flex-grow-1"
                                                        >
                                                            + Add
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={handleClearAllDestinationFilters}
                                                            disabled={destinationFilters.length === 0}
                                                            className="flex-grow-1"
                                                        >
                                                            Clear
                                                        </Button>
                                                    </Col>
                                                </Row>

                                                {/* Display added filters */}
                                                {destinationFilters.length > 0 && (
                                                    <div className="mt-3">
                                                        {destinationFilters.map((filter, index) => (
                                                            <Badge
                                                                key={index}
                                                                bg="primary"
                                                                className="me-2 mb-2"
                                                                style={{ fontSize: '0.9rem', padding: '0.5rem' }}
                                                            >
                                                                {filter.parentDestinationLabel}
                                                                {filter.subcategoryDestinationId && ` > ${filter.subcategoryDestinationLabel}`}
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="text-white p-0 ms-2"
                                                                    onClick={() => handleRemoveDestinationFilter(index)}
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    ×
                                                                </Button>
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Categories (Optional)</Form.Label>
                                            <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '0.5rem' }}>
                                                {categories.length > 0 ? (
                                                    <Row>
                                                        <Col md={3}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                id="cat-all"
                                                                label={<strong>All Categories</strong>}
                                                                checked={selectedCategories.length === categories.length && categories.length > 0}
                                                                onChange={handleAllCategoriesChange}
                                                                className="mb-1"
                                                            />
                                                        </Col>
                                                        {categories.map(category => (
                                                            <Col md={3} key={category._id}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    id={`cat-${category._id}`}
                                                                    label={category.name}
                                                                    checked={selectedCategories.includes(category._id)}
                                                                    onChange={() => handleCategoryChange(category._id)}
                                                                    className="mb-1"
                                                                />
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                ) : (
                                                    <div className="text-muted">No categories available</div>
                                                )}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="align-items-center">
                                    <Col>
                                        <Button
                                            variant="primary"
                                            onClick={handleGenerateReport}
                                            disabled={loading}
                                            className="me-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-file-earmark-text me-2"></i>
                                                    Generate Report
                                                </>
                                            )}
                                        </Button>

                                        {reportData && (
                                            <>
                                                <Button
                                                    variant="success"
                                                    onClick={() => handleExport('csv')}
                                                    disabled={loading}
                                                    className="me-2"
                                                >
                                                    <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                                                    Export CSV
                                                </Button>
                                                <Button
                                                    variant="info"
                                                    onClick={() => handleExport('excel')}
                                                    disabled={loading}
                                                >
                                                    <i className="bi bi-file-earmark-excel me-2"></i>
                                                    Export Excel
                                                </Button>
                                            </>
                                        )}

                                        <Form.Check
                                            type="switch"
                                            id="aggregate-toggle"
                                            label="Aggregate by Item"
                                            checked={aggregateByItem}
                                            onChange={handleAggregateToggle}
                                            className="d-inline-block ms-3"
                                        />
                                    </Col>
                                </Row>
                            </Form>

                            {/* Report Summary */}
                            {reportData && reportData.totals && (
                                <Row className="mb-4">
                                    <Col md={3}>
                                        <div className="bg-primary text-white p-3 rounded">
                                            <h5 className="mb-1">Total Items</h5>
                                            <h3 className="mb-0">{reportData.totals.total_items_issued}</h3>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-success text-white p-3 rounded">
                                            <h5 className="mb-1">Total Quantity</h5>
                                            <h3 className="mb-0">{Number(reportData.totals.total_quantity).toFixed(2)}</h3>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-info text-white p-3 rounded">
                                            <h5 className="mb-1">Total Cost</h5>
                                            <h3 className="mb-0">{formatCurrency(reportData.totals.total_cost)}</h3>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="bg-warning text-white p-3 rounded">
                                            <h5 className="mb-1">Total Weight</h5>
                                            <h3 className="mb-0">{reportData.totals.total_weight_lbs} lbs</h3>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Report Data Table */}
                            {reportData && reportData.results && (
                                <>
                                    <div className="table-responsive">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>Image</th>
                                                    <th>GTIN</th>
                                                    <th>SKU</th>
                                                    <th>Description</th>
                                                    <th>Manufacturer</th>
                                                    {aggregateByItem && <th>Issuances</th>}
                                                    <th>Quantity</th>
                                                    {aggregateByItem
                                                        ? <th>Total Cost</th>
                                                        : <th>Cost</th>
                                                    }
                                                    {!aggregateByItem && <th>Location</th>}
                                                    {!aggregateByItem && <th>Destination</th>}
                                                    {!aggregateByItem && <th>Issue Date</th>}
                                                    {!aggregateByItem && <th>User</th>}
                                                    <th>Weight (lbs)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.results.map((item, index) => (
                                                    <tr key={`${item.stock_item_id || item.id}-${index}`}>
                                                        <td>
                                                            {item.stock_item_image ? (
                                                                <OverlayTrigger
                                                                    placement="right"
                                                                    overlay={
                                                                        <Tooltip className="stock-item-tooltip">
                                                                            <StockItemInfo stockItem={{
                                                                                image: item.stock_item_image,
                                                                                description: item.stock_item_description,
                                                                                sku: item.stock_item_sku,
                                                                                gtin: item.stock_item_gtin,
                                                                                manufacturer_name: item.manufacturer_name,
                                                                            }} />
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <img
                                                                        src={item.stock_item_image}
                                                                        alt="Stock Item Thumbnail"
                                                                        style={{
                                                                            width: '50px',
                                                                            height: '50px',
                                                                            objectFit: 'contain'
                                                                        }}
                                                                    />
                                                                </OverlayTrigger>
                                                            ) : null}
                                                        </td>
                                                        <td>{item.stock_item_gtin}</td>
                                                        <td>{item.stock_item_sku}</td>
                                                        <td>{item.stock_item_description}</td>
                                                        <td>{item.manufacturer_name}</td>
                                                        {aggregateByItem && (
                                                            <td>
                                                                <Badge bg="secondary">{item.issuance_count}</Badge>
                                                            </td>
                                                        )}
                                                        <td>
                                                            <Badge bg="primary">{formatQuantity(item.quantity)}</Badge>
                                                        </td>
                                                        {aggregateByItem
                                                            ? <td>{formatCurrency(item.total_cost)}</td>
                                                            : <td>{formatUnitCost(item.cost)}</td>
                                                        }
                                                        {!aggregateByItem && <td>{item.location}</td>}
                                                        {!aggregateByItem && (
                                                            <td>
                                                                <Badge bg="info">{item.destination}</Badge>
                                                            </td>
                                                        )}
                                                        {!aggregateByItem && <td>{formatDate(item.issue_date)}</td>}
                                                        {!aggregateByItem && <td>{item.username}</td>}
                                                        <td>{item.weight_lbs}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {reportData.total_pages > 1 && (
                                        <PaginationComponent
                                            currentPage={reportData.current_page}
                                            totalItems={reportData.count}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </>
                            )}

                            {reportData && reportData.results && reportData.results.length === 0 && (
                                <Alert variant="info">
                                    No issued inventory items found for the selected criteria.
                                </Alert>
                            )}
        </Container>
    );
}

export default IssuedInventoryReportScreen;